import React, { useState, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/browser';
import CartItems from './CartItems';
import CartAddOns from './CartAddOns';
import Totals from './Totals';
import KlarnaCheckout from './KlarnaCheckout';
import checkoutAnalytics from './checkoutAnalytics';
import Input from '../Elements/Input/Input';
import Button from '../Elements/Button/Button';
import Select from '../Elements/Select/Select';
import Radio from '../Elements/Radio/Radio';
import Checkbox from '../Elements/Checkbox/Checkbox';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import {
	addVoucher,
	voucherInputChange,
	removeVoucher,
	changeCountry,
	changeState,
	changePaymentMethod,
	changeShippingMethod,
	changeAddressInput,
	booleanChange,
	updateKlarna,
	updatePaymentMethod,
	purchase,
} from '../../actions/checkout';
import { unique } from '../../utils/array';
import { purifier } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { fieldValidation } from '../../utils/string';
import {
	paymentMethodsMapping,
	paymentMethodsOrder,
	addressFieldOrder,
} from '../../config';
import s from './Selection.scss';

const Selection = ({ checkout, setup, paymentIcons = [], ...props }) => {
	const { selection, shippingAvailable, paymentUpdating = false } = checkout;
	const { totals, paymentFields, shippingMethods = [] } = selection;
	let { paymentMethods = [] } = selection;
	let states = setup.countries[setup.country].states ? Object.keys(setup.countries[setup.country].states).map((state) => ({ key: state, value: setup.countries[setup.country].states[state] })) : null;
	// US and CA are exceptions to the following variable.
	// CA is already handled and US is irrelevant for goods under 800 USD.
	const nonCustomsCountries = ['AT', 'BE', 'BG', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GI', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'US', 'CA'];
	const removeStates = {
		US: ['PR', 'AS', 'FM', 'GU', 'PW', 'VI', 'MP', 'MH', 'AA', 'AE', 'AP'],
	};
	if (removeStates[setup.country]) {
		states = states.filter((state) => removeStates[setup.country].indexOf(state.key) === -1);
	}

	const [errors, updateErrors] = useState({
		address: {},
		shippingAddress: {},
	});
	const [voucherShowing, toggleVoucher] = useState(false);
	const [analytics, analyticsShown] = useState(false);
	const [selectedPhonePrefix, updatePhonePrefix] = useState(`${setup.countries[setup.country].phonePrefix} (0)`);
	const [selectedShippingPhonePrefix, updateShippingPhonePrefix] = useState(`${setup.countries[setup.country].phonePrefix} (0)`);
	const phonePrefixes = unique(Object.keys(setup.countries).reduce((acc, country) => [...acc, ...(setup.countries[country].phonePrefix || [])], [])).sort((a, b) => a.localeCompare(b)).map((p) => `${p} (0)`);

	let hasShipping = false;
	if (paymentFields?.shippingAddress) {
		Object.keys(paymentFields.shippingAddress).forEach((field) => {
			if (paymentFields?.shippingAddress?.[field]?.visible) hasShipping = true;
		});
	}

	useEffect(() => {
		if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
		if (!analytics && selection.items && selection.items.length > 0) {
			analyticsShown(true);

			checkoutAnalytics(selection.items, setup, selection.totals, checkout.listItemCache);

			// Check if State is already filled in and notify Centra
			const { address = {}, shippingAddress = {} } = checkout.payment;
			if (address?.state || shippingAddress?.state) {
				props.updateKlarna();
			}

			const hasStripePaymentIntents = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
			if (Object.keys(hasStripePaymentIntents).length > 0) props.purchase(true, paymentMethodsMapping.stripePaymentIntents);
		}
	}, [selection]);

	const onToggleVoucher = (e) => {
		e.preventDefault();
		toggleVoucher((prevState) => !prevState);
	};
	const addVoucherHandler = (e) => {
		e.preventDefault();
		props.addVoucher().then(() => props.updateKlarna()).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: voucher not added');
		});
		props.updatePaymentMethod();
	};
	const removeVoucherHandler = (e) => {
		e.preventDefault();
		if (selection.discounts.vouchers.length <= 1) toggleVoucher(false);
		const voucherId = e.currentTarget.dataset.voucher;
		props.removeVoucher(voucherId).then(() => props.updateKlarna()).catch((err) => {
			Sentry.captureException(err);
			console.log('DEBUG: voucher not removed');
		});
		props.updatePaymentMethod();
	};
	const onVoucherInputChange = (id, value) => {
		props.voucherInputChange(value);
		props.updatePaymentMethod();
	};

	const countries = Object.keys(setup.countries).reduce((acc, country) => (country !== '0' ? [...acc, { key: country, value: setup.countries[country].name, disabled: !setup.countries[country].shipTo }] : [...acc]), []);
	const onShippingCountryChange = (id, value) => {
		props.changeAddressInput('shippingAddress', 'country', value);
		props.changeCountry(value);
		props.updatePaymentMethod();
	};
	const onCountryChange = (id, value) => {
		props.changeAddressInput('address', 'country', value);
		onShippingCountryChange(id, value);
	};
	const onShippingStateChange = (id, value) => {
		props.changeAddressInput('shippingAddress', 'state', value);
		props.changeState(value);
	};
	const onStateChange = (id, value) => {
		props.changeAddressInput('address', 'state', value);
		onShippingStateChange(id, value);
	};

	paymentMethods = paymentMethods.sort((a, b) => {
		const posA = paymentMethodsOrder.indexOf(a.paymentMethod) !== -1 ? paymentMethodsOrder.indexOf(a.paymentMethod) : 1000;
		const posB = paymentMethodsOrder.indexOf(b.paymentMethod) !== -1 ? paymentMethodsOrder.indexOf(b.paymentMethod) : 1000;
		return posA - posB;
	});

	const availablePaymentMethods = paymentMethods.reduce((acc, method) => {
		if (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents) return acc;
		const hasPaymentIcon = paymentIcons.filter((pI) => pI.centraUri === method.paymentMethod)[0];
		if (hasPaymentIcon) return [...acc, { key: method.paymentMethod, value: translate(setup, hasPaymentIcon.title), icons: hasPaymentIcon.icons || [] }];
		return [...acc, { key: method.paymentMethod, value: method.name }];
	}, []) || [];

	const hasKlarna = paymentMethods.filter((method) => method.paymentMethod === paymentMethodsMapping.klarnaCheckout) || false;
	const hasAdyenDropIn = paymentMethods.reduce((acc, method) => (method.paymentMethod.indexOf(paymentMethodsMapping.adyenDropIn) !== -1 ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
	const hasStripeCheckout = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripeCheckout ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;
	const hasStripePaymentIntents = paymentMethods.reduce((acc, method) => (method.paymentMethod === paymentMethodsMapping.stripePaymentIntents ? { ...acc, [method.paymentMethod]: method } : acc), {}) || false;

	const onPaymentMethodChange = (value) => {
		props.changePaymentMethod(value);
	};

	const shippingNames = setup.statics.shippingNames || [];
	const shippingMethodOrder = shippingNames.map((m) => m.shippingURI);
	let availableShippingMethods = [];
	if (shippingMethods) {
		availableShippingMethods = shippingMethods.reduce((acc, method) => {
			const shipName = shippingNames.filter((shippingName) => shippingName.shippingURI === method.shippingMethod)[0];
			return [...acc, { key: method.shippingMethod, value: shipName?.shippingTitle ? translate(setup, shipName.shippingTitle) : '', disabled: shippingAvailable[method.shippingMethod] === false, info: shipName?.shippingInfo ? translate(setup, shipName.shippingInfo) : '' }];
		}, []).sort((a, b) => {
			const aIndex = shippingMethodOrder.indexOf(a.key) === -1 ? 100 : shippingMethodOrder.indexOf(a.key);
			const bIndex = shippingMethodOrder.indexOf(b.key) === -1 ? 100 : shippingMethodOrder.indexOf(b.key);
			if (aIndex > bIndex) return 1;
			if (aIndex < bIndex) return -1;
			return 0;
		});
	}

	const onShippingMethodChange = (value) => {
		props.changeShippingMethod(value);
	};

	const getIdAndGroup = (id) => {
		if (id.indexOf('shippingAddress') !== -1) return ['shippingAddress', String(id).replace('shippingAddress-', '')];
		if (id.indexOf('address') !== -1) return ['address', String(id).replace('address-', '')];
		return [false, String(id)];
	};

	const onPhonePrefixChange = (id, value) => {
		if (id === 'address-phoneNumberPrefix') updatePhonePrefix(value);
		else updateShippingPhonePrefix(value);
	};

	const onAddressInputChange = (id, value) => {
		const [group, field] = getIdAndGroup(id);
		if (field === 'phoneNumber') value = `${selectedPhonePrefix} ${value}`.replace(/\s*\(0\)\s*/, ' ');

		updateErrors((prevState) => {
			if (group) {
				const { [field]: removed, ...nextState } = prevState[group];
				return { ...prevState, [group]: { ...nextState } };
			}
			const { [field]: removed, ...nextState } = prevState;
			return { ...nextState };
		});

		if (field === 'address1' && fieldValidation(value, field)) {
			updateErrors((prevState) => (
				{
					...prevState,
					[group]: {
						...prevState[group],
						[field]: translate(setup, `error${fieldValidation(value, field)}`),
					},
				}
			));

			const address1Limited = value.slice(0, 50);
			const address2 = `${value.slice(address1Limited.lastIndexOf(' ')).trim()}${(` ${checkout.payment?.[group]?.address2}` || '')}`;
			value = value.slice(0, address1Limited.lastIndexOf(' '));

			if (fieldValidation(address2.trim(), 'address2')) {
				updateErrors((prevState) => (
					{
						...prevState,
						[group]: {
							...prevState[group],
							address2: translate(setup, `error${fieldValidation(address2.trim(), 'address2')}`),
						},
					}
				));
			} else {
				setTimeout(() => {
					updateErrors((prevState) => {
						const { [field]: remove, ...groupFields } = prevState[group];
						return (
							{
								...prevState,
								[group]: { ...groupFields },
							}
						);
					});
				}, 1000);
			}

			props.changeAddressInput(group, 'address2', address2.trim().slice(0, 50));
			document.getElementById(`${group}-address2`).focus();
		}

		if ((field === 'address2' || field === 'city') && value.length > 50) {
			updateErrors((prevState) => (
				{
					...prevState,
					[group]: {
						...prevState[group],
						[field]: translate(setup, `error${fieldValidation(value, field)}`),
					},
				}
			));
			value = value.slice(0, 50);
		}

		props.changeAddressInput(group, field, value);
	};

	const onAddressInputBlur = (id, value, all = false) => {
		const [group, field] = getIdAndGroup(id);

		onAddressInputChange(id, value);

		if (
			fieldValidation(value, field) // Is field invalid?
			&& ( // And is required
				!selection?.paymentFields?.[group]?.[field] || selection?.paymentFields?.[group]?.[field]?.required // Is in a group.
				|| !selection?.paymentFields?.[field] || selection?.paymentFields?.[field]?.required // Is not in a group.
				|| (field === 'state' && states) || field === 'country'
			)
			&& (group !== 'shippingAddress' || (group === 'shippingAddress' && checkout.useShippingAddress)) // And is address or is using shipping address.
			&& field !== 'phoneNumberPrefix' // Exclude specific fields
		) {
			updateErrors((prevState) => {
				if (group) {
					return {
						...prevState,
						[group]: {
							...prevState[group],
							[field]: translate(setup, `error${fieldValidation(value, field)}`),
						},
					};
				}
				return {
					...prevState,
					[field]: translate(setup, `error${fieldValidation(value, field)}`),
				};
			});
			return true;
		}

		if (!fieldValidation(value, field) && !all) {
			// Is valid update methods.
			props.updatePaymentMethod();
			if (field === 'email' || field === 'zipCode') props.updateKlarna();
		}
		return false;
	};

	const onBooleanChange = (id, value) => {
		props.booleanChange(id);
		if (selection.paymentMethod === paymentMethodsMapping.klarnaCheckout && id === 'subscribeToNewsletter') props.updateKlarna();
		if (id === 'useShipping' && value === false) {
			props.changeAddressInput('address', 'country', checkout.payment.shippingAddress.country);
		}
		props.updatePaymentMethod();
	};

	const onPurchase = (e) => {
		e.preventDefault();

		// Ensure that we are using data from the fields as it may be autofilled.
		const $inputs = e.currentTarget.querySelectorAll('input,select');
		let hasErrors = false;

		[...$inputs].forEach(($input) => {
			const id = $input.getAttribute('id');
			let value = $input.value;
			if ($input.type === 'checkbox' && !$input.checked) value = 0;
			const returnErrors = onAddressInputBlur(id, value, true);
			if (returnErrors) hasErrors = true;
		});

		if (hasErrors || Object.keys(errors.address).length > 0 || Object.keys(errors.shippingAddress).length > 0 || Object.keys(errors).length > 2) return;

		props.purchase();
	};

	return (
		<main role="main">
			{
				!selection.items || selection.items.length === 0 ? (
					<div className={s.selection}>
						<h1 className={s.selection_part_title}>{ translate(setup, 'checkoutNoItems') }</h1>
					</div>
				) : (
					<div className={s.selection}>
						<div className={s.selection_summary}>
							<div className={s.selection_part}>
								<div className={s.selection_part_inner}>
									<ErrorBoundary message="Product data missing" debugData={selection}>
										<h2 className={s.selection_part_title}>{ translate(setup, 'checkoutYourItemsTitle') }</h2>
										<CartItems currentPage="selection" />
										<div className={s.selection_part_totals}>
											<div className={s.selection_part_totals_voucher}>
												{
													voucherShowing || (selection.discounts && selection.discounts.vouchers.length > 0) ? (
														<form onSubmit={addVoucherHandler}>
															<Input id="voucherCode" type="text" placeholder={translate(setup, 'checkoutVoucherCode')} value={checkout.vouchers.currentValue} inputChange={onVoucherInputChange} width="calc(100% - 120px)" />
															<Button type="submit" text={translate(setup, 'checkoutVoucherUse')} />
															{ checkout.vouchers.error ? <div className={s.selection_part_totals_voucher_error}>{ translate(setup, 'checkoutVoucherError') }</div> : null }
															{
																selection.discounts && selection.discounts.vouchers.length > 0 && (
																	<div className={s.selection_part_totals_voucher_added}>
																		{
																			selection.discounts.vouchers.map((voucher) => (
																				<a key={voucher.voucher} className={s.selection_part_totals_voucher_added_row} href="#removeVoucher" data-voucher={voucher.voucher} onClick={removeVoucherHandler}>
																					<span>{voucher.voucher}</span>
																					<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
																						<g fill="none" strokeLinecap="square">
																							<line x1="3.455" x2="12.545" y1="3.455" y2="12.545" stroke="#2B3337" strokeWidth="2" />
																							<line x1="3.455" x2="12.545" y1="12.545" y2="3.455" stroke="#2B3337" strokeWidth="2" />
																						</g>
																					</svg>
																				</a>
																			))
																		}
																	</div>
																)
															}
														</form>
													) : (
														<a className={s.selection_part_totals_voucher_toggle} href="#toggleVoucher" onClick={onToggleVoucher}>
															<span>{ translate(setup, 'checkoutToggleVoucher') }</span>
															<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
																<g fill="none" strokeLinecap="square">
																	<line x1="3" x2="13" y1="8" y2="8" stroke="#2B3337" strokeWidth="2" />
																	<line x1="8" x2="8" y1="3" y2="13" stroke="#2B3337" strokeWidth="2" />
																</g>
															</svg>
														</a>
													)
												}
											</div>
										</div>
										{
											!setup.campaignType && (
												<div className={[s.selection_part, s.selection_part__addons].join(' ')}>
													<CartAddOns setup={setup} items={selection.items} isSelection />
													<p className={s.selection_part_description}>{ translate(setup, 'checkoutAddOnsDescription') }</p>
													<h2 className={s.selection_part_title}>{ translate(setup, 'checkoutAddOnsTitle') }</h2>
												</div>
											)
										}
										{ setup.statics.checkoutCustomerService ? <div className={s.selection_part_customerService} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutCustomerService')) }} /> : null }
									</ErrorBoundary>
								</div>
							</div>
						</div>
						{
							setup.unableToSendToCountry ? (
								<div className={s.selection_info}>
									<div className={s.selection_part}>
										<h2 className={s.selection_part_title}>{ translate(setup, 'checkoutDeliveryTitleChangeCountry') }</h2>
										<div className={s.selection_part_country}>
											<Select id="country" selected={setup.country} options={countries} inputChange={onCountryChange} />
										</div>
									</div>
								</div>
							) : (
								<div className={s.selection_info}>
									<div className={s.selection_part}>
										<h2 className={s.selection_part_title}>{ translate(setup, 'checkoutDeliveryTitle') }</h2>
										<div className={[s.selection_part_country, s.selection_part_country__shippingMethods].join(' ')}>
											<Select id="country" selected={setup.country} options={countries} inputChange={onCountryChange} />
											{
												states ? (
													<div className={s.selection_part_address_row}>
														<Select id="address-state" selected={checkout.payment.address?.state} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, 'stateLabel')} inputChange={onStateChange} inputBlur={onAddressInputBlur} isError={errors.address?.state} />
													</div>
												) : null
											}
										</div>
										{ !totals.taxAdded && setup.country === 'CA' && setup.statics.checkoutShippingCAD && <div className={s.selection_part_shippingMessage} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutShippingCanada')) }} /> }
										{/* totals.taxAdded && setup.country === 'CA' && <div className={s.selection_part_shippingMessage} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'taxAddedCheckoutShippingCanada').replace('%percent%', `${totals.taxPercent}%`)) }} /> */}
										{ nonCustomsCountries.indexOf(setup.country) === -1 && <div className={s.selection_part_shippingMessage} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, 'checkoutShippingOther')) }} /> }
										<ErrorBoundary message="Shipping methods" debugData={selection}>
											<div className={s.selection_part_shipping}>
												<Radio id="shipping" selected={selection.shippingMethod} options={availableShippingMethods} inputChange={onShippingMethodChange} />
											</div>
										</ErrorBoundary>
										<Totals setup={setup} selection={selection} />
									</div>
									<div className={s.selection_part}>
										{
											Object.keys(hasStripePaymentIntents).length > 0 && (
												<div className={s.selection_stripe}>
													<div id="stripePaymentIntentsContainer" />
													<div className={s.selection_stripe_paymentButton} id="stripeCheckoutButton" />
													<h2 className={s.selection_part_title}>{ translate(setup, 'checkoutExpressPaymentTitle') }</h2>
												</div>
											)
										}
										<h2 className={s.selection_part_title}>{ translate(setup, 'checkoutPaymentTitle') }</h2>
										<div className={s.selection_part_payment}>
											<ErrorBoundary message="Payment methods" debugData={selection}>
												<div className={s.selection_part_payment_choice}>
													<Radio id="payment" selected={selection.paymentMethod} options={availablePaymentMethods} inputChange={onPaymentMethodChange} />
												</div>
											</ErrorBoundary>
										</div>
										<form onSubmit={onPurchase}>
											<div className={s.selection_part_address}>
												<ErrorBoundary message="Billing address" debugData={paymentFields}>
													{
														addressFieldOrder.map((field) => {
															if (!paymentFields?.address?.[field]?.visible) return null;
															if (field === 'state') {
																if (states) {
																	return (
																		<div key={`address-${field}`} className={s.selection_part_address_row}>
																			<Select id={`address-${field}`} selected={checkout.payment.address[field]} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, `${field}Label`)} inputChange={onShippingStateChange} inputBlur={onAddressInputBlur} isError={errors.address?.[field]} />
																		</div>
																	);
																}
																return null;
															}
															const fieldEvents = {
																inputChange: onAddressInputChange,
																inputBlur: onAddressInputBlur,
																isError: errors.address?.[field],
															};
															if (field === 'phoneNumber') {
																return (
																	<div className={[s.selection_part_address_row, s.selection_part_address_row__inlineFields].join(' ')}>
																		<Select id="address-phoneNumberPrefix" selected={selectedPhonePrefix} options={phonePrefixes} inputChange={onPhonePrefixChange} label={translate(setup, 'phonePrefixLabel')} />
																		<Input id="address-phoneNumber" type="tel" value={checkout.payment.address.phoneNumber ? checkout.payment.address.phoneNumber.slice(checkout.payment.address.phoneNumber.lastIndexOf(' ') + 1) : ''} label={translate(setup, 'phoneNumberLabel')} {...fieldEvents} />
																	</div>
																);
															}
															return (
																<div key={`address-${field}`} className={s.selection_part_address_row}>
																	<Input id={`address-${field}`} type={field === 'email' ? 'email' : 'text'} value={checkout.payment.address[field]} label={translate(setup, `${field}Label`)} {...fieldEvents} />
																</div>
															);
														})
													}
													{
														hasShipping && (
															<div className={s.selection_part_address_row}>
																<Select id="address-country" selected={checkout.payment.address.country} options={countries} label={translate(setup, 'countryLabel')} inputChange={onCountryChange} />
															</div>
														)
													}
												</ErrorBoundary>
											</div>
											{
												hasShipping && (
													<div className={s.selection_part_shippingAddress}>
														<ErrorBoundary message="Shipping address" debugData={paymentFields}>
															<Checkbox id="useShipping" label={translate(setup, 'checkoutAlternateAddress')} selected={checkout.useShippingAddress} inputChange={onBooleanChange} />
															{
																checkout.useShippingAddress && addressFieldOrder.map((field) => {
																	if (!paymentFields?.address?.[field]?.visible) return null;
																	if (field === 'state') {
																		if (states) {
																			return (
																				<div key={`shippingAddress-${field}`} className={s.selection_part_address_row}>
																					<Select id={`shippingAddress-${field}`} selected={checkout.payment.shippingAddress[field]} options={[{ key: '', value: translate(setup, 'chooseState') }, ...states]} label={translate(setup, `${field}Label`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.shippingAddress?.[field]} />
																				</div>
																			);
																		}
																		return null;
																	}
																	if (field === 'phoneNumber') {
																		return (
																			<div className={[s.selection_part_address_row, s.selection_part_address_row__inlineFields].join(' ')}>
																				<Select id="shippingAddress-phoneNumberPrefix" selected={selectedShippingPhonePrefix} options={phonePrefixes} inputChange={onPhonePrefixChange} label={translate(setup, 'phonePrefixLabel')} />
																				<Input id="shippingAddress-phoneNumber" type="tel" value={checkout.payment.shippingAddress.phoneNumber ? checkout.payment.shippingAddress.phoneNumber.slice(checkout.payment.shippingAddress.phoneNumber.indexOf(' ') + 1) : ''} label={translate(setup, 'phoneNumberLabel')} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.shippingAddress?.[field]} />
																			</div>
																		);
																	}
																	return (
																		<div key={`shippingAddress-${field}`} className={s.selection_part_address_row}>
																			<Input id={`shippingAddress-${field}`} type={field === 'email' ? 'email' : 'text'} value={checkout.payment.shippingAddress[field]} label={translate(setup, `${field}Label`)} inputChange={onAddressInputChange} inputBlur={onAddressInputBlur} isError={errors.shippingAddress?.[field]} />
																		</div>
																	);
																})
															}
															{
																checkout.useShippingAddress && (
																	<div className={s.selection_part_address_row}>
																		<Select id="shippingAddress-country" selected={checkout.payment.shippingAddress.country} options={countries} label={translate(setup, 'countryLabel')} inputChange={onShippingCountryChange} />
																	</div>
																)
															}
														</ErrorBoundary>
													</div>
												)
											}
											{
												paymentFields?.termsAndConditions?.visible && (
													<div className={s.selection_part_purchase}>
														<ErrorBoundary message="Complete payment" debugData={checkout.payment}>
															<Checkbox id="subscribeToNewsletter" label={translate(setup, 'subscribeToNewsletter')} selected={checkout.payment.address.newsletter} inputChange={onBooleanChange} />
															<Checkbox id="termsAndConditions" innerHTML={translate(setup, 'checkoutAcceptTerms')} selected={checkout.payment.termsAndConditions} inputChange={onBooleanChange} isError={errors?.termsAndConditions} />
															<span className={s.selection_part_purchase_totalHeader}>{ translate(setup, 'checkoutTotalOrder') }</span>
															<h2 className={s.selection_part_purchase_total}>{ totals.grandTotalPrice }</h2>
															{ selection.paymentMethod.indexOf('paypal') !== -1 && <div className={s.selection_part_purchase_error}>{ translate(setup, 'paypalMessage') }</div> }
															{ checkout.selectionError && <div className={s.selection_part_purchase_error}>{ translate(setup, 'checkoutGeneralError') }</div> }
															{ checkout.selectionErrorStock && <div className={s.selection_part_purchase_error}>{ translate(setup, 'checkoutStockError') }</div> }
															{ (!checkout.adyenInitiated || checkout.selectionError || checkout.selectionErrorStock) && <Button text={translate(setup, 'checkoutPayment')} type="submit" /> }
														</ErrorBoundary>
													</div>
												)
											}
										</form>
										{
											hasKlarna.length > 0 && (
												<div className={s.selection_part_purchase}>
													<ErrorBoundary message="Klarna checkout" debugData={selection}>
														{ selection.paymentMethod === paymentMethodsMapping.klarnaCheckout ? <Checkbox id="subscribeToNewsletter" label={translate(setup, 'subscribeToNewsletter')} selected={checkout.payment.address.newsletter} inputChange={onBooleanChange} /> : null }
														<KlarnaCheckout paymentMethod={selection.paymentMethod} />
													</ErrorBoundary>
												</div>
											)
										}
										{
											Object.keys(hasAdyenDropIn).length > 0 && (
												<div className={[s.selection_paymentContainer, (paymentUpdating ? s.selection_paymentContainer__updating : null)].join(' ')} id="adyenDropInContainer" />
											)
										}
										{
											Object.keys(hasStripeCheckout).length > 0 && (
												<div className={[s.selection_paymentContainer, (paymentUpdating ? s.selection_paymentContainer__updating : null)].join(' ')} id="stripeCheckoutContainer" />
											)
										}
									</div>
								</div>
							)
						}
					</div>
				)
			}
		</main>
	);
};

const mapState = ({ checkout, environment }, { data }) => (
	{ checkout, setup: environment.setup, data, paymentIcons: environment.paymentIcons }
);

export default hot(module)(connect(mapState, {
	addVoucher,
	voucherInputChange,
	removeVoucher,
	changeCountry,
	changeState,
	changePaymentMethod,
	changeShippingMethod,
	changeAddressInput,
	booleanChange,
	updateKlarna,
	updatePaymentMethod,
	purchase,
})(Selection));
